import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { formatDate } from '@/util/dateUtils';

import { useComparisonMode } from './useComparisonMode';
import { useHumanAgentsQueryVariables } from './useHumanAgentsQueryVariables';
import type { HumanAgentsTimeseriesPoint } from '../components/HumanAgentsOverview/humanAgentsChartConfig';
import { selectAnalyticsDates } from '../redux/selectors';
import { isSingleDayRange } from '../utils/formatDateRangeLabel';
import { getComparisonDates } from '../utils/getComparisonDates';

interface HumanAgentsTimeseriesRow {
  time_bucket: string;
  total_conversations: number | null;
  csat_human_agent_sum: number | null;
  csat_human_agent_count: number | null;
  resolution_seconds_sum: number | null;
  resolution_seconds_count: number | null;
  user_rating_sum: number | null;
  user_rating_count: number | null;
}

interface QueryResponse {
  rows: HumanAgentsTimeseriesRow[];
}

const TIMESERIES_FIELDS = `
  time_bucket
  total_conversations
  csat_human_agent_sum
  csat_human_agent_count
  resolution_seconds_sum
  resolution_seconds_count
  user_rating_sum
  user_rating_count
`;

export const GET_HUMAN_AGENTS_CONVERSATIONS_TIMESERIES = gql`
  query HumanAgentsConversationsTimeseries(
    $start_time: timestamp!
    $end_time: timestamp!
    $desk_ids: _uuid
    $channels: _text
    $channel_types: _text
    $agent_ids: _text
    $csat_human_agent: _int4
    $min_num_user_messages: Int
    $is_test: Boolean
    $user_rating: _int4
  ) {
    rows: human_agents_conversations_timeseries(
      args: {
        start_time: $start_time
        end_time: $end_time
        desk_ids: $desk_ids
        channels: $channels
        channel_types: $channel_types
        agent_ids: $agent_ids
        csat_human_agent: $csat_human_agent
        min_num_user_messages: $min_num_user_messages
        is_test: $is_test
        user_rating: $user_rating
      }
    ) {
      ${TIMESERIES_FIELDS}
    }
  }
`;

const GET_HUMAN_AGENTS_CONVERSATIONS_TIMESERIES_COMPARISON = gql`
  query HumanAgentsConversationsTimeseriesComparison(
    $start_time: timestamp!
    $end_time: timestamp!
    $desk_ids: _uuid
    $channels: _text
    $channel_types: _text
    $agent_ids: _text
    $csat_human_agent: _int4
    $min_num_user_messages: Int
    $is_test: Boolean
    $user_rating: _int4
  ) {
    rows: human_agents_conversations_timeseries(
      args: {
        start_time: $start_time
        end_time: $end_time
        desk_ids: $desk_ids
        channels: $channels
        channel_types: $channel_types
        agent_ids: $agent_ids
        csat_human_agent: $csat_human_agent
        min_num_user_messages: $min_num_user_messages
        is_test: $is_test
        user_rating: $user_rating
      }
    ) {
      ${TIMESERIES_FIELDS}
    }
  }
`;

const safeAvg = (sum: number | null, count: number | null): number => {
  if (!sum || !count) {
    return 0;
  }
  return sum / count;
};

// A day with conversations but no ratings has no score; null breaks the line instead of drawing 0.
const csatAvg = (sum: number | null, count: number | null): number | null =>
  count ? Number(safeAvg(sum, count).toFixed(2)) : null;

const toPoint = (row: HumanAgentsTimeseriesRow): HumanAgentsTimeseriesPoint => ({
  date: formatDate(row.time_bucket, 'yyyy-MM-dd'),
  userCsat: csatAvg(row.user_rating_sum, row.user_rating_count),
  userReviews: row.user_rating_count ?? 0,
  aiCsat: csatAvg(row.csat_human_agent_sum, row.csat_human_agent_count),
  aiReviews: row.csat_human_agent_count ?? 0,
  conversations: row.total_conversations ?? 0,
  resolutionTimeSeconds: Math.round(
    safeAvg(row.resolution_seconds_sum, row.resolution_seconds_count)
  ),
});

export const useHumanAgentsTimeseries = () => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  // Charts aren't rendered for single-day ranges; skip the round-trip.
  const isSingleDay = isSingleDayRange(startDate, endDate);

  const { variables, skip } = useHumanAgentsQueryVariables();
  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: comparisonVariables } = useHumanAgentsQueryVariables(comparisonDates);

  const { loading, data, error } = useQuery<QueryResponse>(
    GET_HUMAN_AGENTS_CONVERSATIONS_TIMESERIES,
    { variables, skip: skip || isSingleDay }
  );

  const { loading: comparisonLoading, data: comparisonData } = useQuery<QueryResponse>(
    GET_HUMAN_AGENTS_CONVERSATIONS_TIMESERIES_COMPARISON,
    { variables: comparisonVariables, skip: skip || !isComparing || isSingleDay }
  );

  const points = useMemo<HumanAgentsTimeseriesPoint[]>(
    () => (data?.rows ?? []).map(toPoint),
    [data]
  );

  const comparisonPoints = useMemo<HumanAgentsTimeseriesPoint[]>(
    () => (comparisonData?.rows ?? []).map(toPoint),
    [comparisonData]
  );

  return {
    isLoading: loading || (isComparing && comparisonLoading),
    error,
    data: points,
    comparisonData: comparisonPoints,
  };
};
